import { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Button,
  ButtonBase,
  CircularProgress,
  Link,
  List,
  ListItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { isApiError } from "@bw/shared/api";
import type { MyBookingInvitationResponse } from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { PlayerPeekDialog } from "../../../app/components/PlayerPeekDialog";
import { useMyBookingInvitations } from "../hooks/useMyBookingInvitations";
import { useRespondBookingInvitation } from "../hooks/useRespondBookingInvitation";
import { useRespondBookingInvitationGroup } from "../hooks/useRespondBookingInvitationGroup";
import { visuallyHidden } from "../../../common/visuallyHidden";

/** The honest inviter display name for a card — the server-resolved name when it
 *  resolved, else null (so the caller renders a neutral framing). NEVER the raw
 *  inviter_id (no-UID-in-UI). */
function inviterName(invite: MyBookingInvitationResponse): string | null {
  return invite.inviter_display_name || null;
}

/** The inviter reference for an accessible label — the resolved name, else a
 *  neutral "a player" (NEVER the raw inviter_id). Used to give each card's
 *  Accept/Decline a distinguishing accessible name (#1496) so a screen-reader user
 *  hears WHICH invite the button acts on, not a bare "Accept" / "Decline". */
function inviterLabel(invite: MyBookingInvitationResponse): string {
  return inviterName(invite) ?? "a player";
}

/** Honest inviter line for a SINGLE card. When the name resolved, it is rendered
 *  as a peek button (transparent button, looks like text) so clicking it opens the
 *  player's profile peek dialog. When it didn't resolve we fall back to a neutral
 *  "A player invited you" as plain text — never the raw inviter_id (no-UID-in-UI). */
function InviterLine({
  invite,
  onPeek,
}: {
  invite: MyBookingInvitationResponse;
  onPeek: (userId: string, displayName: string) => void;
}) {
  const name = inviterName(invite);
  if (name) {
    return (
      <Typography component="p">
        <ButtonBase
          type="button"
          onClick={() => onPeek(invite.inviter_id, name)}
          aria-label={`View ${name}`}
          sx={{ borderRadius: 1, font: "inherit", fontWeight: 700 }}
        >
          {name}
        </ButtonBase>{" "}
        invited you
      </Typography>
    );
  }
  return <Typography component="p">A player invited you</Typography>;
}

/** Inviter line for a GROUPED card — "{name} invited you to play". When the name
 *  resolved, renders it as a peek button (same pattern as InviterLine). Neutral
 *  "A player" fallback for an unresolved name, never the raw uid. */
function GroupInviterLine({
  invite,
  onPeek,
}: {
  invite: MyBookingInvitationResponse;
  onPeek: (userId: string, displayName: string) => void;
}) {
  const name = inviterName(invite);
  if (name) {
    return (
      <Typography component="p">
        <ButtonBase
          type="button"
          onClick={() => onPeek(invite.inviter_id, name)}
          aria-label={`View ${name}`}
          sx={{ borderRadius: 1, font: "inherit", fontWeight: 700 }}
        >
          {name}
        </ButtonBase>{" "}
        invited you to play
      </Typography>
    );
  }
  return <Typography component="p">A player invited you to play</Typography>;
}

/** Venue-local "Tue, Jun 20 · 5:00 PM" for an invite's booking start, best-effort.
 *  Never leaks the raw ISO string or "Invalid Date" (mirrors BookingDetailPage's
 *  whenLabel). */
function whenLabel(startTime: string): string {
  try {
    const formatted = new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(startTime));
    if (/invalid/i.test(formatted)) return "Time unavailable";
    return formatted;
  } catch {
    return "Time unavailable";
  }
}

/**
 * Map a respond failure to honest inline copy (never a blank crash). Keyed
 * PRIMARILY by HTTP STATUS so a renamed backend error code can never silently
 * blank the UI:
 *   - 409 BOOKING_NOT_MODIFIABLE → the booking can no longer be joined.
 *   - any other 409 (already responded / DuplicateInvitationError / unknown) → the
 *     invite is no longer available.
 *   - 404 → the invite is no longer available.
 *   - 403 → you can't respond to this invite.
 *   - else → a generic try-again.
 */
function mapRespondError(err: unknown): string {
  if (isApiError(err)) {
    if (err.status === 409) {
      if (err.code === "BOOKING_NOT_MODIFIABLE") {
        return "This booking can no longer be joined.";
      }
      return "That invite is no longer available — it may have been withdrawn or already responded to.";
    }
    if (err.status === 404) {
      return "That invite is no longer available — it may have been withdrawn.";
    }
    if (err.status === 403) {
      return "You can't respond to this invite.";
    }
  }
  return "We couldn't update that invite. Please try again.";
}

/** A rendered inbox entry: either a single null-group invite, or a collapsed
 *  recurring group (≥1 invite sharing one non-null recurring_group_id). */
type InboxEntry =
  | { kind: "single"; invite: MyBookingInvitationResponse }
  | {
      kind: "group";
      recurringGroupId: string;
      invites: MyBookingInvitationResponse[];
    };

/**
 * Collapse the pending invitations into inbox entries: invitations sharing a
 * NON-NULL `recurring_group_id` collapse into ONE group entry (positioned at the
 * group's FIRST appearance in the list); invitations with a NULL group each stay
 * their own single entry. Pure presentation grouping — no derived business state.
 */
function groupInvites(invites: MyBookingInvitationResponse[]): InboxEntry[] {
  const entries: InboxEntry[] = [];
  const groupIndexById = new Map<string, number>();

  for (const invite of invites) {
    const gid = invite.recurring_group_id;
    if (!gid) {
      entries.push({ kind: "single", invite });
      continue;
    }
    const existing = groupIndexById.get(gid);
    if (existing === undefined) {
      groupIndexById.set(gid, entries.length);
      entries.push({ kind: "group", recurringGroupId: gid, invites: [invite] });
    } else {
      (
        entries[existing] as { invites: MyBookingInvitationResponse[] }
      ).invites.push(invite);
    }
  }
  return entries;
}

/** Earliest start_time / latest end_time across a group's slots (the rendered
 *  range). ISO strings sort lexicographically for a fixed offset, but compare as
 *  timestamps to be robust to mixed offsets. */
function groupRange(invites: MyBookingInvitationResponse[]): {
  earliestStart: string;
  latestEnd: string;
} {
  let earliestStart = invites[0].start_time;
  let latestEnd = invites[0].end_time;
  for (const inv of invites) {
    if (
      new Date(inv.start_time).getTime() < new Date(earliestStart).getTime()
    ) {
      earliestStart = inv.start_time;
    }
    if (new Date(inv.end_time).getTime() > new Date(latestEnd).getTime()) {
      latestEnd = inv.end_time;
    }
  }
  return { earliestStart, latestEnd };
}

/**
 * Court invites inbox (/dropin/court-invites).
 *
 * The caller's incoming PENDING court-booking invitations, GROUPED by recurring
 * group. A 5-slot recurring booking arrives as 5 invitations sharing one
 * `recurring_group_id`; they collapse into ONE card — "{inviter} invited you to
 * play", the facility, and a time RANGE (earliest start … latest end, which conveys
 * the multi-slot span) — with ONE Accept (accept the whole group) + ONE Decline. A
 * NULL-group invite stays its own card with the single accept/decline. Each row shows
 * WHO invited you (server-resolved display name — never a raw user_id, rendered as a
 * peek button) and venue-local times. The FACILITY NAME is a link to /facility/:id.
 *
 * Accept (group OR single) joins the booking(s) and opens a booking detail at
 * /book/{facility_id}/bookings/{booking_id}. A group navigates to the first row
 * the server actually accepted, because dead slots may be skipped. Decline removes
 * the invite(s) (the list refetches via invalidation) and stays on the page. A
 * respond failure surfaces inline and leaves the list intact; an empty group
 * accept is an honest no-live-bookings state and never navigates.
 *
 * THIN presentation layer: every value is server truth rendered verbatim; the only
 * client logic is the recurring-group collapse, the honest inviter-name fallback,
 * and the status→copy error map.
 */
export function BookingInvitesInboxPage() {
  const navigate = useNavigate();
  const invitesQuery = useMyBookingInvitations();
  const respond = useRespondBookingInvitation();
  const respondGroup = useRespondBookingInvitationGroup();

  const [respondError, setRespondError] = useState<string | null>(null);
  const [peek, setPeek] = useState<{
    userId: string;
    displayName: string;
  } | null>(null);

  const invites = invitesQuery.data ?? [];
  const entries = groupInvites(invites);
  const busy = respond.isPending || respondGroup.isPending;

  function handleRespondSingle(
    invite: MyBookingInvitationResponse,
    accept: boolean,
  ) {
    setRespondError(null);
    respond.mutate(
      {
        facilityId: invite.facility_id,
        bookingId: invite.booking_id,
        invitationId: invite.invitation_id,
        accept,
      },
      {
        onSuccess: () => {
          if (accept) {
            navigate(
              `/book/${invite.facility_id}/bookings/${invite.booking_id}`,
            );
          }
          // Decline stays on the page; the row leaves via list invalidation.
        },
        onError: (err) => setRespondError(mapRespondError(err)),
      },
    );
  }

  function handleRespondGroup(
    entry: Extract<InboxEntry, { kind: "group" }>,
    accept: boolean,
  ) {
    setRespondError(null);
    respondGroup.mutate(
      { recurringGroupId: entry.recurringGroupId, accept },
      {
        onSuccess: (acceptedInvitations) => {
          if (accept) {
            const firstAccepted = acceptedInvitations[0];
            if (!firstAccepted) {
              setRespondError(
                "There are no live bookings available to join in this invitation.",
              );
              return;
            }
            const destination = entry.invites.find(
              (invite) => invite.booking_id === firstAccepted.booking_id,
            );
            if (!destination) {
              setRespondError(
                "You joined the reservation, but we couldn't open its booking. Refresh your invites and try again.",
              );
              return;
            }
            navigate(
              `/book/${destination.facility_id}/bookings/${destination.booking_id}`,
            );
          }
          // Decline stays on the page; the group leaves via list invalidation.
        },
        onError: (err) => setRespondError(mapRespondError(err)),
      },
    );
  }

  return (
    <Page>
      <Stack spacing={2} useFlexGap>
        <Typography component="h1" sx={{ fontSize: "1.375rem", fontWeight: 700 }}>
          Court invites
        </Typography>

        {invitesQuery.isLoading ? (
          <Stack
            direction="row"
            spacing={1.5}
            role="status"
            aria-live="polite"
            sx={{ py: 4, alignItems: "center", justifyContent: "center" }}
          >
            <CircularProgress size={22} />
            <Typography color="text.secondary">
              Loading your invites…
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {respondError ? (
              <Alert severity="error" role="alert">
                {respondError}
              </Alert>
            ) : null}

            {entries.length === 0 ? (
              <Alert severity="info">You have no pending court invites.</Alert>
            ) : (
              <Paper variant="outlined">
                <List disablePadding>
                  {entries.map((entry) =>
                    entry.kind === "single" ? (
                      <ListItem
                        key={entry.invite.invitation_id}
                        divider
                        sx={{
                          px: 2,
                          py: 1.5,
                          gap: 2,
                          alignItems: { xs: "stretch", sm: "center" },
                          flexDirection: { xs: "column", sm: "row" },
                        }}
                      >
                        <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                          <InviterLine
                            invite={entry.invite}
                            onPeek={(userId, displayName) =>
                              setPeek({ userId, displayName })
                            }
                          />
                          <Typography color="text.secondary" variant="body2">
                            {entry.invite.court_name} ·{" "}
                            <Link
                              component={RouterLink}
                              to={`/facility/${entry.invite.facility_id}`}
                            >
                              {entry.invite.facility_name}
                            </Link>
                          </Typography>
                          <Typography color="text.secondary" variant="body2">
                            <time dateTime={entry.invite.start_time}>
                              {whenLabel(entry.invite.start_time)}
                            </time>
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                          {/* Per-card accessible name (#1496): a SR navigating by
                          buttons hears "Accept invite from {who} for {court}", not
                          a bare "Accept". Built from data already on the card —
                          never the raw inviter_id, never fabricated text. */}
                          <Button
                            type="button"
                            variant="contained"
                            size="small"
                            onClick={() =>
                              handleRespondSingle(entry.invite, true)
                            }
                            disabled={busy}
                            aria-label={`Accept invite from ${inviterLabel(
                              entry.invite,
                            )} for ${entry.invite.court_name}`}
                          >
                            Accept
                          </Button>
                          <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            onClick={() =>
                              handleRespondSingle(entry.invite, false)
                            }
                            disabled={busy}
                            aria-label={`Decline invite from ${inviterLabel(
                              entry.invite,
                            )} for ${entry.invite.court_name}`}
                          >
                            Decline
                          </Button>
                        </Stack>
                      </ListItem>
                    ) : (
                      <GroupCard
                        key={entry.recurringGroupId}
                        entry={entry}
                        busy={busy}
                        onAccept={() => handleRespondGroup(entry, true)}
                        onDecline={() => handleRespondGroup(entry, false)}
                        onPeek={(userId, displayName) =>
                          setPeek({ userId, displayName })
                        }
                      />
                    ),
                  )}
                </List>
              </Paper>
            )}
          </Stack>
        )}
      </Stack>

      {peek ? (
        <PlayerPeekDialog
          userId={peek.userId}
          displayName={peek.displayName}
          onClose={() => setPeek(null)}
        />
      ) : null}
    </Page>
  );
}

/** One collapsed recurring group → one card: the inviter line (peek button), the
 *  facility (link to /facility/:id), the time RANGE (earliest start … latest end as
 *  two <time> nodes — this conveys the multi-slot span), and a single Accept /
 *  Decline that respond for the whole group. */
function GroupCard({
  entry,
  busy,
  onAccept,
  onDecline,
  onPeek,
}: {
  entry: Extract<InboxEntry, { kind: "group" }>;
  busy: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onPeek: (userId: string, displayName: string) => void;
}) {
  const first = entry.invites[0];
  const { earliestStart, latestEnd } = groupRange(entry.invites);
  return (
    <ListItem
      divider
      sx={{
        px: 2,
        py: 1.5,
        gap: 2,
        alignItems: { xs: "stretch", sm: "center" },
        flexDirection: { xs: "column", sm: "row" },
      }}
    >
      <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
        <GroupInviterLine invite={first} onPeek={onPeek} />
        <Typography color="text.secondary" variant="body2">
          <Link component={RouterLink} to={`/facility/${first.facility_id}`}>
            {first.facility_name}
          </Link>
        </Typography>
        <Typography color="text.secondary" variant="body2">
          <time dateTime={earliestStart}>{whenLabel(earliestStart)}</time>
          {" – "}
          <time dateTime={latestEnd}>{whenLabel(latestEnd)}</time>
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1}>
        {/* Per-card accessible name (#1496): a grouped (recurring) invite has no
            single court, so the facility distinguishes it — "Accept invite from
            {who} for {facility}". Built from data already on the card. */}
        <Button
          type="button"
          variant="contained"
          size="small"
          onClick={onAccept}
          disabled={busy}
          aria-label={`Accept invite from ${inviterLabel(first)} for ${
            first.facility_name
          }`}
        >
          Accept
        </Button>
        <Button
          type="button"
          variant="outlined"
          size="small"
          onClick={onDecline}
          disabled={busy}
          aria-label={`Decline invite from ${inviterLabel(first)} for ${
            first.facility_name
          }`}
        >
          Decline
        </Button>
      </Stack>
    </ListItem>
  );
}
